import React, { useMemo } from "react";
import { Edit2, Power, Eye, User, CreditCard } from "lucide-react";
import { DataTable, TableActionButton } from "@/components/ui";
import {
  EMPLOYMENT_STATUS_COLORS, EMPLOYMENT_STATUS_LABELS,
  EMPLOYMENT_TYPE_LABELS, getDepartmentLabel,
} from "../../constants/hrConstants";
import { API_BASE_URL } from "@/services/api";

// ─── DEV TESTING FLAG ────────────────────────────────────────────────────────
// Set to `true` to allow the Pay Salary button on any day (bypasses last-day check).
// Set back to `false` before deploying to production.
const DEV_BYPASS_PAY_DATE = true;
// ─────────────────────────────────────────────────────────────────────────────

export function EmployeeTable({ employees, onEdit, onView, onToggleStatus, onPay }) {
  const columns = useMemo(() => [
    {
      header: "Employee ID",
      key: "id",
      className: "w-[8%] min-w-[70px]",
      render: (emp) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
          #{emp.id?.slice(0, 6)}
        </span>
      )
    },
    {
      header: "Name",
      key: "name",
      className: "w-[18%] min-w-[180px]",
      render: (emp) => (
        <div className="flex flex-col min-w-0">
          <span className="text-brand-dark font-medium tracking-tight text-sm leading-tight truncate">
            {emp.name || "No Name"}
          </span>
          <span className="text-[11px] text-slate-500 truncate mt-0.5">
            {emp.phone || "No Phone"} <span className="mx-1 text-slate-300">|</span> {emp.email?.toLowerCase() || "No Email"}
          </span>
        </div>
      )
    },
    {
      header: "Designation",
      key: "designation",
      className: "w-[12%] min-w-[110px]",
      render: (emp) => (
        <span className="text-[11px] text-slate-700 capitalize whitespace-nowrap">
          {emp.designation?.replace("_", " ") || "—"}
        </span>
      )
    },
    {
      header: "Department",
      key: "department",
      className: "w-[12%] min-w-[110px]",
      render: (emp) => (
        <span className="text-[11px] text-slate-700 capitalize whitespace-nowrap">
          {getDepartmentLabel(emp.department)}
        </span>
      )
    },
    {
      header: "Type",
      key: "type",
      className: "w-[8%] min-w-[80px]",
      render: (emp) => (
        <span className="text-[11px] text-slate-700 capitalize whitespace-nowrap">
          {EMPLOYMENT_TYPE_LABELS[emp.employmentType] || emp.employmentType || "—"}
        </span>
      )
    },
    {
      header: "Joining Date",
      key: "joiningDate",
      className: "w-[10%] min-w-[100px]",
      render: (emp) => (
        <span className="text-[11px] text-slate-700 whitespace-nowrap">
          {emp.joiningDate ? new Date(emp.joiningDate).toLocaleDateString("en-IN") : "—"}
        </span>
      )
    },
    {
      header: "Documents",
      key: "documents",
      className: "w-[12%] min-w-[120px]",
      render: (emp) => {
        if (!emp.documents || !emp.documents.length) return <span className="text-[11px] text-slate-400">—</span>;
        return (
          <div className="flex flex-wrap gap-1 max-w-[160px]">
            {emp.documents.map((doc, idx) => (
              <a
                key={idx}
                href={doc.url?.startsWith('/') ? `${API_BASE_URL}${doc.url}` : (doc.url || "#")}
                download={doc.name}
                className="text-[9px] font-semibold bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded border border-blue-100 hover:bg-blue-100 truncate max-w-[80px] shadow-sm transition-colors"
                title={doc.name}
                onClick={(e) => e.stopPropagation()}
              >
                {doc.name}
              </a>
            ))}
          </div>
        );
      }
    },
    {
      header: "Salary",
      key: "salary",
      className: "w-[10%] min-w-[90px]",
      render: (emp) => (
        <span className="text-[11px] text-slate-700 whitespace-nowrap">
          ₹{Number(emp.salary || 0).toLocaleString("en-IN")}
        </span>
      )
    },
    {
      header: "Status",
      key: "status",
      className: "w-[8%] min-w-[80px]",
      render: (emp) => {
        const colorClass = EMPLOYMENT_STATUS_COLORS[emp.status] || "bg-slate-100 text-slate-500 border-slate-200";
        return (
          <span className={`inline-flex items-center px-2 py-0.5 rounded border text-[10px] font-semibold ${colorClass}`}>
            {EMPLOYMENT_STATUS_LABELS[emp.status] || emp.status}
          </span>
        );
      }
    },
    {
      header: "Actions",
      key: "actions",
      className: "w-[14%] min-w-[140px]",
      render: (emp) => {
        const today = new Date();
        const lastDate = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
        const isLastDay = DEV_BYPASS_PAY_DATE || today.getDate() === lastDate;

        let isDisabled = emp.status !== "active" || !isLastDay;

        let btnTitle = "Pay Salary";
        if (emp.status !== "active") {
          btnTitle = "Activate employee to pay salary";
        } else if (!isLastDay) {
          const monthName = today.toLocaleDateString("en-US", { month: "long" });
          btnTitle = `Disbursement is only active on the last day of the month (${monthName} ${lastDate}th)`;
        }

        return (
          <div className="flex items-center gap-1.5 justify-end">
            <TableActionButton
              icon={CreditCard}
              variant="success"
              onClick={() => onPay(emp)}
              disabled={isDisabled}
              title={btnTitle}
            />
            <TableActionButton
              icon={Eye}
              variant="primary"
              onClick={() => onView(emp)}
              title="View Details"
            />
            <TableActionButton
              icon={Edit2}
              variant="primary"
              onClick={() => onEdit(emp)}
              title="Edit Profile"
            />
            <TableActionButton
              icon={Power}
              variant={emp.status === "active" ? "danger" : "success"}
              onClick={() => onToggleStatus(emp._id || emp.id)}
              title={emp.status === "active" ? "Deactivate Employee" : "Activate Employee"}
            />
          </div>
        );
      }
    }
  ], [onEdit, onView, onToggleStatus, onPay]);

  if (!employees.length) {
    return (
      <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
        <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
        <p className="text-slate-500 font-medium text-[13px]">No employees found</p>
        <p className="text-slate-400 text-[12px] mt-1">Try adjusting your filters</p>
      </div>
    );
  }

  return (
    <DataTable
      columns={columns}
      data={employees}
      tableClassName="min-w-[900px]"
    />
  );
}
